import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { startImportDocumentMetadataListProcessing } from '~/api/documentMetadata';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Table } from '~/eds/src/modules/Table/Table';
import { BulkImportDocumentMetadataType } from '~/enums/BulkImportDocumentMetadataType';
import { useIntl } from 'react-intl';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import Text from '~/ui/components/Text/Text';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';
import { parseApiError } from '~/utils/parseApiError';

function SummaryStep(props) {
  const {
    dataTestid,
    stepCommonWizardProps,
    data,
    clientId,
    moveToStep,
    handleImportedJob = () => {},
  } = props;
  const { previewHeaders, previewItems, importId } = data;
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);

  const renderBody = () => {
    const columns = previewHeaders.map((header, index) => {
      const key = `col${index}`;
      return {
        key,
        title: header,
        mapCellProps: (d) => ({ text: d[key] }),
        minWidth: 's',
        maxWidth: 'l',
        width: 'm',
      };
    });

    const addColumnKeyValuePairsToDataItem = (dataItem, valuesOfRow) => {
      const addColumnKeyValuePairToDataItem = (
        dataItem,
        value,
        valueIndexInRow,
      ) => {
        const columnKey = columns[valueIndexInRow].key;
        return { ...dataItem, [columnKey]: value };
      };

      return valuesOfRow.reduce(addColumnKeyValuePairToDataItem, dataItem);
    };

    const data = previewItems.map((valuesOfRow, rowIndex) => {
      const dataItem = { id: `${rowIndex}` };
      return addColumnKeyValuePairsToDataItem(dataItem, valuesOfRow);
    });

    return (
      <FlexLayout space={2} flexDirection="column">
        <FlexLayout space={3} flexDirection="column" alignItems="center">
          {isLoading ? (
            <h4>
              {intl.formatMessage({
                id: 'SummaryStep.importingTitle',
                defaultMessage: 'Importing Document Metadata List',
                description: 'Title shown while importing document metadata',
              })}
            </h4>
          ) : (
            <>
              <Text variant="subtitle">
                {intl.formatMessage({
                  id: 'SummaryStep.reviewColumnsTitle',
                  defaultMessage: 'Review Columns',
                  description: 'Title for reviewing columns section',
                })}
              </Text>
              <Text color="gray-600">
                {intl.formatMessage({
                  id: 'SummaryStep.sampleRowsDescription',
                  defaultMessage:
                    'Below are sample rows from the file you have uploaded.',
                  description:
                    'Description explaining the preview table content',
                })}
              </Text>
            </>
          )}
        </FlexLayout>
        {isLoading ? (
          <LoadingSpinner size="m" />
        ) : (
          <Table
            name={intl.formatMessage({
              id: 'SummaryStep.previewTableName',
              defaultMessage: 'Preview Items Table',
              description: 'Name for the preview table',
            })}
            columns={columns}
            data={data}
            options={{
              enableControls: false,
              enableExportXlsx: false,
              enableManageColumns: false,
              enableSearch: false,
              enableSelectRows: false,
            }}
          />
        )}
      </FlexLayout>
    );
  };

  const backButton = {
    promise: () => moveToStep(BulkImportDocumentMetadataType.Upload),
    text: intl.formatMessage({
      id: 'SummaryStep.backToUploadButton',
      defaultMessage: 'Back To Upload',
      description: 'Button to go back to upload step',
    }),
    disabled: false,
  };

  const handleApiError = (errorResponse) => {
    const error = parseApiError(errorResponse);
    showToast(ERROR, error.message);
  };

  const importDocument = async () => {
    setIsLoading(true);
    try {
      await startImportDocumentMetadataListProcessing({ clientId, importId });
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'SummaryStep.processingSuccessMessage',
          defaultMessage: 'Document Metadata List is being processed.',
          description: 'Success message when import processing starts',
        }),
      );
      stepCommonWizardProps.onClose();
      handleImportedJob(importId);
    } catch (errorResponse) {
      handleApiError(errorResponse);
      setIsLoading(false);
    }
  };

  const actionButton = {
    promise: importDocument,
    text: intl.formatMessage({
      id: 'SummaryStep.importDocumentButton',
      defaultMessage: 'Import Document',
      description: 'Button to start document import',
    }),
    disabled: isLoading,
  };

  return (
    <WizardPageLayout
      {...stepCommonWizardProps}
      isContentFill={true}
      isContainer={false}
      currentStep={BulkImportDocumentMetadataType.Summary}
      actionButton={actionButton}
      backButton={backButton}
      children={<div data-testid={dataTestid}>{renderBody()}</div>}
    />
  );
}

SummaryStep.propTypes = {
  dataTestid: PropTypes.string.isRequired,
  stepCommonWizardProps: PropTypes.object.isRequired,
  moveToStep: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  clientId: PropTypes.string.isRequired,
};

export default SummaryStep;
